import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { EmptyState } from '~/components/empty-state'
import { FoodFormDialog } from '~/components/food-form-dialog'
import { FoodRow, ListSkeleton, MealRow } from '~/components/food-search'
import { FoodSheet } from '~/components/food-sheet'
import { ChevronLeftIcon, FoodIcon, PlusIcon } from '~/components/icons'
import { MealPage } from '~/components/meal-page'
import { RecipeFormDialog } from '~/components/recipe-form-dialog'
import { useDataService, useDataState } from '~/data/data-context'
import {
  askingToLog,
  placedMeals,
  placedOwn,
  usePlacing,
  useMyFoods,
  useMyMeals,
} from '~/data/food-store'
import { foodKey, nowTime, type Food, type Meal } from '~/data/nutrition'
import { todayKey } from '~/data/weighins'
import { withoutDialog } from '~/lib/dialogs'

interface OwnFoodsSearch {
  /**
   * Something to open as the page arrives, named as `meal:<id>` or `food:<id>`. It is what
   * saving from a shared link lands on — the nutrition spec says saving leaves the shared page
   * for the saved thing's own page, and a food's own page on the web is reached from here.
   */
  readonly open?: string
}

export const Route = createFileRoute('/diary_/foods')({
  validateSearch: (search: Record<string, unknown>): OwnFoodsSearch =>
    typeof search.open === 'string' && /^(meal|food):.+$/.test(search.open)
      ? { open: search.open }
      : {},
  component: OwnFoods,
})

/**
 * The account's own foods and recipes, per the spec's Own foods and Recipes: where each is
 * created, opened to edit, and deleted. A page under the diary rather than the corner card —
 * a food's form is a page of fields, and the card is three hundred and twenty pixels wide.
 */
function OwnFoods() {
  const service = useDataService()
  const navigate = useNavigate()
  const { open } = Route.useSearch()
  // Both are the account's own, read off what this device holds — so a food saved on the page
  // stands in the list in the same breath as the save.
  const foods = useMyFoods()
  const heldMeals = useMyMeals()
  const heldLogs = useDataState().mealLogs
  /*
   * What these lists are placed for — `/specs/features/food-order.spec.md`. The asking is made
   * once, as the page opens, and held, so nothing re-places itself under a pointer reading
   * down it.
   */
  const [asking] = useState(() => askingToLog(todayKey(), nowTime()))
  const placing = usePlacing(asking)
  const meals =
    heldMeals === null || heldLogs === null || placing === null
      ? null
      : placedMeals(heldMeals.meals, heldLogs, placing)
  const [making, setMaking] = useState<'food' | 'recipe' | 'meal' | null>(null)
  const [opened, setOpened] = useState<Food | null>(null)
  /** The meal open on its own page, where it is read, changed and logged. */
  const [viewedMeal, setViewedMeal] = useState<Meal | null>(null)
  const [about, setAbout] = useState<Food | null>(null)

  /*
   * What the address asks to be open — how a shared link's saving lands on the saved thing's
   * own page. Read off the address as the page draws rather than opened by an effect: the
   * address is what says it is open, so a reload lands in the same place and closing it is
   * the address changing rather than two answers to keep in step.
   */
  const wanted = open ?? ''
  const wantedId = wanted.slice(wanted.indexOf(':') + 1)
  const askedMeal = wanted.startsWith('meal:')
    ? ((meals ?? []).find((meal) => meal.id === wantedId) ?? null)
    : null
  const askedFood = wanted.startsWith('food:')
    ? ((foods ?? []).find((food) => food.id === wantedId) ?? null)
    : null

  /** Whichever of the two is open: one pressed here, or the one the address asked for. */
  const viewingMeal = viewedMeal ?? askedMeal
  const editingFood = opened ?? askedFood

  /** Closing what the address asked for is the address letting go of it. */
  const forgetAsked = () => {
    if (open !== undefined) {
      void navigate({ to: '/diary/foods', search: {}, replace: true })
    }
  }

  /*
   * A write has already gone into the mirror the lists read, so there is nothing to ask for
   * again — but the server has the last word on what a recipe comes to, so the feed is pulled
   * and whatever it says takes the mirror's place.
   */
  const refresh = () => {
    void service.refresh()
  }
  const closeAll = () => {
    setMaking(null)
    setOpened(null)
    setViewedMeal(null)
    forgetAsked()
  }
  /** A food of your own is met here too, so it is opened from here too. */
  const aboutProps = (food: Food) => ({
    onAbout: () => {
      setAbout(food)
    },
  })

  /*
   * Placed the way every list the account logs out of is placed — and the foods logged before
   * are what place them, so the page waits on those as well as on the lists themselves: see
   * the skeleton below. `placedOwn` is what the logging panel's own library tabs use.
   *
   * Worked out once per set of inputs rather than on every render: the page re-renders each
   * time a dialog opens or closes, and a library of several hundred is the case this is for.
   */
  const recipes = useMemo(
    () =>
      foods === null || placing === null
        ? []
        : placedOwn(
            foods.filter((food) => food.recipe !== null),
            placing,
          ),
    [foods, placing],
  )
  const plain = useMemo(
    () =>
      foods === null || placing === null
        ? []
        : placedOwn(
            foods.filter((food) => food.recipe === null),
            placing,
          ),
    [foods, placing],
  )

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-2">
        <Link
          to="/diary"
          search={withoutDialog}
          aria-label="Back to the diary"
          className="icon-btn -ml-2"
        >
          <ChevronLeftIcon aria-hidden className="size-7" />
        </Link>
        <h1 className="flex-1 text-3xl font-semibold">Own foods</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-ink"
          onClick={() => {
            setMaking('food')
          }}
        >
          <PlusIcon aria-hidden className="size-5" />
          New food
        </button>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm ring-1 ring-white/15 transition-colors hover:bg-hover"
          onClick={() => {
            setMaking('recipe')
          }}
        >
          <PlusIcon aria-hidden className="size-5" />
          New recipe
        </button>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm ring-1 ring-white/15 transition-colors hover:bg-hover"
          onClick={() => {
            setMaking('meal')
          }}
        >
          <PlusIcon aria-hidden className="size-5" />
          New meal
        </button>
      </div>

      {foods === null || meals === null || placing === null ? (
        <ListSkeleton label="Loading your foods" />
      ) : foods.length === 0 && meals.length === 0 ? (
        <EmptyState
          icon={<FoodIcon className="size-8" />}
          title="No foods of your own"
          detail="A food you create here is offered wherever you log, a recipe is a food made of others, and a meal is foods logged together."
        />
      ) : (
        <>
          {plain.length > 0 && (
            <section className="space-y-2">
              <h2 className="px-3 text-xs font-medium tracking-wider text-ink-muted uppercase">
                Foods
              </h2>
              <ul className="space-y-0.5">
                {plain.map((food) => (
                  <FoodRow
                    key={foodKey(food)}
                    food={food}
                    onPress={() => {
                      setOpened(food)
                    }}
                    {...aboutProps(food)}
                  />
                ))}
              </ul>
            </section>
          )}
          {recipes.length > 0 && (
            <section className="space-y-2">
              <h2 className="px-3 text-xs font-medium tracking-wider text-ink-muted uppercase">
                Recipes
              </h2>
              <ul className="space-y-0.5">
                {recipes.map((food) => (
                  <FoodRow
                    key={foodKey(food)}
                    food={food}
                    onPress={() => {
                      setOpened(food)
                    }}
                    {...aboutProps(food)}
                  />
                ))}
              </ul>
            </section>
          )}
          {meals.length > 0 && (
            <section className="space-y-2">
              <h2 className="px-3 text-xs font-medium tracking-wider text-ink-muted uppercase">
                Meals
              </h2>
              <ul className="space-y-0.5">
                {meals.map((meal) => (
                  <MealRow
                    key={meal.id}
                    meal={meal}
                    onPress={() => {
                      setViewedMeal(meal)
                    }}
                  />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {(making === 'food' || (editingFood !== null && editingFood.recipe === null)) && (
        <FoodFormDialog
          food={editingFood}
          onClose={closeAll}
          onSaved={(saved) => {
            closeAll()
            refresh()
            // Made, not edited: it opens on itself, which is the thing somebody came here to
            // have. Landing back on the list left them hunting for it among everything else.
            if (making === 'food') {
              setAbout(saved)
            }
          }}
          onDeleted={() => {
            closeAll()
            refresh()
          }}
        />
      )}
      {about !== null && (
        <FoodSheet
          food={about}
          mine
          onClose={() => {
            setAbout(null)
          }}
          onSaved={() => {
            setAbout(null)
            refresh()
          }}
        />
      )}
      {(making === 'recipe' || (editingFood !== null && editingFood.recipe !== null)) && (
        <RecipeFormDialog
          recipe={editingFood}
          onClose={closeAll}
          onSaved={(saved) => {
            closeAll()
            refresh()
            // As a food does: a recipe is made in order to be logged.
            if (making === 'recipe') {
              setAbout(saved)
            }
          }}
          onDeleted={() => {
            closeAll()
            refresh()
          }}
        />
      )}
      {/*
       * A meal's page, where it is read, changed and logged — the nutrition spec, Meals. A
       * meal pressed here opens on it, and a new one is made on it. Logging from here lands
       * on today, there being no day behind this page for it to land on.
       */}
      {(making === 'meal' || viewingMeal !== null) && (
        <MealPage
          key={viewingMeal?.id ?? 'new'}
          meal={viewingMeal}
          exists={viewingMeal !== null}
          day={todayKey()}
          onClose={() => {
            closeAll()
            refresh()
          }}
          onLogged={() => {
            closeAll()
            refresh()
          }}
          onDeleted={() => {
            closeAll()
            refresh()
          }}
        />
      )}
    </div>
  )
}
